/**
 * The flight layer — chips and cards in motion, above the felt.
 *
 * ============================== WHAT THIS IS ===================================
 * The Director announces each step before its state lands (`onStep`), and this layer
 * draws the movement in between: the deal leaving the deck, a bet's chips sliding to
 * the bet spot, every bet gathering under the pot pill, the pot flying to the winner's
 * stack, a folded hand tumbling into the muck. It measures the real elements by their
 * `data-td` anchors with `getBoundingClientRect`, spawns a copy at the source, and
 * animates it to the destination with the Web Animations API. When the flight ends the
 * static element, which the presented state has meanwhile placed at that same pixel,
 * takes over with a short settle.
 *
 * The felt never reflows during a flight: nothing in it changes until the step lands,
 * and every flight is `position: absolute` inside `.td-flight`, which is
 * `pointer-events: none` and covers `.td-app` — the whole table, so a pot can reach the
 * hero zone below the felt.
 *
 * What flies is drawn by the same components as the felt — `PlayingCard` for cards,
 * `ChipStacks` (over the kit's `ChipStack`) for money — rule 5: one deck, one felt.
 *
 * ## Phase 3b — flights with character
 *
 * Phase 3's flights were straight point-to-point slides of a single chip top. Now:
 * a bet is the denomination breakdown of the amount, leaves as a short pile, arcs and
 * swells a little; the collect converges under the pill a beat per seat, clockwise from
 * the button; the pot arcs to the winner's NUMBER and a side pot follows the main; a
 * dealt card turns from the deck's angle to the seat's as it flies and the hero's cards
 * grow to hero size only over the last third; the muck tumbles and fades over its last
 * half. Every fraction is `SHAPE` and every millisecond is `Durations`, both from
 * `pace.ts`; nothing here carries a number of its own.
 *
 * Reduced motion: no travel. The copy appears at the destination and fades in over the
 * same duration (a muck fades where it lies), so the rhythm of a hand is kept and the
 * movement is not.
 * ===============================================================================
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { TableState } from "@engine/table-state";
import type { Step } from "./director";
import { SHAPE, type Durations } from "./pace";
import { PlayingCard } from "../components/PlayingCard";
import { ChipStacks, breakdown, type Stack } from "./ChipStacks";
import { useMoney } from "../lib/MoneyContext";
import type { TauntItem } from "./lines";
import { stickerUrl } from "../art/stickers";

/* ------------------------------------------------------------------ bus --- */

/**
 * How steps reach the layer without the scene knowing about the Director.
 *
 * `TopDownTable` takes a `flights` prop of this type and nothing else about timing;
 * the route wires `director.onStep` to `bus.emit`. Anything else in the scene that wants
 * to know a step began — the deck, the payoff — subscribes too.
 */
export class FlightBus<S extends { dur: number; land: number } = Step, B = TableState> {
  private readonly subs = new Set<(step: S, before: B) => void>();
  /**
   * The step nobody was listening for. The very first step of a session — the
   * shuffle — is announced in the same call that gives the table its first state, so
   * the layer that would draw it has not rendered yet. It is kept until a subscriber
   * arrives, and handed over if the step is still in progress.
   */
  private pending: { step: S; before: B; at: number } | null = null;

  emit(step: S, before: B): void {
    if (this.subs.size === 0) { this.pending = { step, before, at: performance.now() }; return; }
    for (const s of this.subs) s(step, before);
  }
  subscribe(fn: (step: S, before: B) => void): () => void {
    this.subs.add(fn);
    const p = this.pending;
    if (p) {
      this.pending = null;
      const elapsed = performance.now() - p.at;
      if (elapsed < Math.max(p.step.dur, p.step.land)) fn(p.step, p.before);
    }
    return () => { this.subs.delete(fn); };
  }
}

/* -------------------------------------------------------------- geometry --- */

export interface Point { x: number; y: number }

/** A flying thing. `from`/`to` are centres, in the layer's own pixels. */
export interface Flight {
  id: number;
  kind: "card" | "chips";
  from: Point;
  to: Point;
  /** The width the copy is drawn at. For a card, its final width. */
  width: number;
  dur: number;
  /** Fade at the destination instead of travelling. */
  fade: boolean;
  /** Milliseconds to wait before starting. */
  delay: number;
  /** Chips: the piles that travel. */
  stacks?: Stack[];
  /** Cards: the angle to arrive at, in degrees, and whether this is the hero's grower. */
  rotateTo?: number;
  growFrom?: number;
  /** The muck: tumble, shrink and fade over the last part of the flight. */
  muck?: boolean;
  /** How much the copy swells at mid-flight (1 = none). */
  swell: number;
  /** Mid-flight lift, in pixels. */
  arc: number;
}

/** The centre of an element, relative to the layer. Null if it is not on the page. */
export function centre(el: Element | null, origin: DOMRect): Point | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { x: r.left + r.width / 2 - origin.left, y: r.top + r.height / 2 - origin.top };
}

/** Like `centre`, for a box that may be empty: its anchor point still means something. */
export function point(el: Element | null, origin: DOMRect): Point | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2 - origin.left, y: r.top + r.height / 2 - origin.top };
}

export function widthOf(el: Element | null, fallback: number): number {
  if (!el) return fallback;
  const w = el.getBoundingClientRect().width;
  return w > 0 ? w : fallback;
}

/** A custom property read as pixels, or the fallback. */
export function lengthVar(el: Element | null, name: string, fallback: number): number {
  if (!el) return fallback;
  const v = parseFloat(getComputedStyle(el).getPropertyValue(name));
  return Number.isFinite(v) && v > 0 ? v : fallback;
}

/**
 * Where things are on this table, by seat.
 *
 * Every selector here is a `data-td` attribute `TopDownTable` sets for exactly this
 * purpose, so the layer does not depend on class names the stylesheet might rename.
 */
class Anchors {
  private readonly origin: DOMRect;
  constructor(private readonly root: HTMLElement) {
    this.origin = root.getBoundingClientRect();
  }
  private q(sel: string): Element | null { return this.root.querySelector(sel); }
  /** The deck at the dealer's spot: cards come from it and the muck goes to it. */
  dealer(): Point {
    const deck = this.q("[data-td=deck]");
    return point(deck, this.origin) ?? centre(this.q("[data-td=board]"), this.origin)
      ?? { x: this.origin.width / 2, y: this.origin.height * 0.3 };
  }
  /** One chip's diameter on this felt. */
  chip(): number { return lengthVar(this.q("[data-td=pot]"), "--chip", 24); }
  /** Where the pot's chips lie, under the pill — never the pill's text. */
  potChips(): Point | null {
    return point(this.q("[data-td=potchips]"), this.origin) ?? centre(this.q("[data-td=pot]"), this.origin);
  }
  /** Where a seat's cards lie: the peek for an opponent, the hand for the hero. */
  cards(seat: number, hero: boolean): { at: Point | null; width: number; angle: number } {
    const el = hero ? this.q("[data-td=hand]") : this.q(`[data-td=seat][data-seat="${seat}"] [data-td=peek]`);
    const card = el?.querySelector(".gc") ?? null;
    const declared = el ? parseFloat(getComputedStyle(el).getPropertyValue(hero ? "--hero" : "--peek-w")) : NaN;
    const fallback = Number.isFinite(declared) && declared > 0 ? declared : (hero ? 62 : 24);
    /*
     * An empty peek is a 0x0 box, but it is a 0x0 box exactly where the cards go. A
     * peek that is `display: none` — portrait hides the face-down peek altogether — has
     * no box at all; the cards go to the player's portrait instead.
     */
    const shown = !!el && el.getClientRects().length > 0;
    const at = shown ? point(el, this.origin)
      : hero ? null : centre(this.q(`[data-td=seat][data-seat="${seat}"] [data-td=av]`), this.origin);
    return { at, width: widthOf(card, fallback), angle: hero ? -6 : -5 };
  }
  /** The seat's stack NUMBER — where money arrives and leaves. Never the face or the name. */
  stackNum(seat: number, hero: boolean): Point | null {
    /*
     * PHASE 7 (found by box 3's seeded-hand proof, in BOTH views): the hero is looked
     * up on their SEAT when there is no corner plate. Phase 5 box 0(b) sat the player
     * at the table like everybody else and removed `[data-td=heroplate]`, and this
     * kept asking for it — so since Phase 5 the player's own blinds and bets never flew
     * to their bet spot and a pot they won never flew to their stack. The corner plate
     * is still tried first, so nothing that has one changes.
     */
    const own = `[data-td=seat][data-seat="${seat}"] [data-td=stacknum]`;
    const sel = hero ? "[data-td=heroplate] [data-td=stacknum]" : own;
    return centre(this.q(sel), this.origin)
      ?? (hero ? centre(this.q(own), this.origin) : null)
      ?? centre(this.q(hero ? "[data-td=heroplate]" : `[data-td=seat][data-seat="${seat}"] [data-td=av]`), this.origin);
  }
  bet(seat: number): Point | null {
    return point(this.q(`[data-td=bet][data-seat="${seat}"] .td-chips`), this.origin)
      ?? point(this.q(`[data-td=bet][data-seat="${seat}"]`), this.origin);
  }

  /* ---------------------------------------------------- Phase 6: the bubble --- */

  /** The layer's own box, which is `.td-app` — what a bubble has to stay inside. */
  bounds(): Box {
    return { l: 0, t: 0, r: this.origin.width, b: this.origin.height };
  }

  /** A seat's portrait: where a bubble points, and how big the thing it clears is. */
  seatAvatar(seat: number): { at: Point; size: number } | null {
    const el = this.q(`[data-td=seat][data-seat="${seat}"] [data-td=av]`);
    const at = centre(el, this.origin);
    if (!at || !el) return null;
    return { at, size: el.getBoundingClientRect().height || 40 };
  }

  /** Which way is away from the middle of the felt, as a unit vector. */
  outward(from: Point): Point {
    const felt = this.q(".td-felt");
    const c = felt ? centre(felt, this.origin) : { x: this.origin.width / 2, y: this.origin.height / 2 };
    if (!c) return { x: 0, y: -1 };
    const dx = from.x - c.x, dy = from.y - c.y;
    const len = Math.hypot(dx, dy) || 1;
    return { x: dx / len, y: dy / len };
  }

  /**
   * What a bubble may not cover, in two grades.
   *
   * HARD is the Phase 2 review's demand made literal: a card is information and a
   * bubble that covers one has taken it away. So are the board, the pot, the NAME
   * PLATES and the BETS, and the plates are here because of a picture: the first run of
   * this placer put "Nothing fancy about it." across Ezra's stack, which read `51,400`
   * instead of `31,400`. Phase 5's whole box 0(c) was making that number the biggest
   * thing on the felt; covering it with a joke is worse than not making the joke.
   *
   * The Phase 2 note said a bubble crossing a neighbour's plate was survivable, and it
   * was RIGHT — about a bubble that could not move. This one can.
   *
   * SOFT is the portraits. A bubble clipping the top of a face at nine-handed loses
   * nothing a player is reading, and making it hard costs bubbles that could have been
   * drawn.
   */
  obstacles(): { hard: Box[]; soft: Box[] } {
    const boxes = (sel: string): Box[] =>
      [...this.root.querySelectorAll(sel)]
        .map((n) => n.getBoundingClientRect())
        .filter((r) => r.width > 0.5 && r.height > 0.5)
        .map((r) => ({
          l: r.left - this.origin.left, t: r.top - this.origin.top,
          r: r.right - this.origin.left, b: r.bottom - this.origin.top,
        }));
    return {
      hard: [
        ...boxes(".gc"), ...boxes("[data-td=board]"), ...boxes("[data-td=pot]"),
        ...boxes(".td-tag"), ...boxes("[data-td=bet]"),
        /*
         * PHASE 7: a standing character's FACE (the saloon view). A bubble over a face
         * is worse than one over a card — it hides who is talking — so it is hard. The
         * top-down table has no such anchor, so for it this list is unchanged.
         */
        ...boxes("[data-td=face]"),
        /*
         * PHASE 8 BOX 0(1): the dealer button (Phase 7 question 12). It says whose deal
         * it is, and a remark laid over the D had taken that away — the Phase 7 box-0
         * shot put "Well now, look at that." across most of the disc. One selector, both
         * views: the disc carries `data-td=dbtn` in top-down and in the saloon.
         */
        ...boxes("[data-td=dbtn]"),
      ],
      soft: boxes("[data-td=av]"),
    };
  }
}

/** A rectangle in the layer's own pixels. */
interface Box { l: number; t: number; r: number; b: number }

const overlaps = (a: Box, b: Box) =>
  a.r > b.l + 0.5 && b.r > a.l + 0.5 && a.b > b.t + 0.5 && b.b > a.t + 0.5;
const within = (a: Box, b: Box) => a.l >= b.l - 0.5 && a.r <= b.r + 0.5 && a.t >= b.t - 0.5 && a.b <= b.b + 0.5;

/* ---------------------------------------------------------------- layer --- */

let nextId = 1;
/** A fresh id for a flight a second planner builds (Phase 8: the blackjack table's). */
export const flightId = (): number => nextId++;

/** Seats in the order the dealer would gather them: clockwise from the button. */
function clockwiseFromButton(seats: number[], button: number, n: number): number[] {
  return [...seats].sort((a, b) => ((a - button + n) % n) - ((b - button + n) % n));
}

/**
 * Turn a step into flights. Pure over the measured anchors, so it can be read as the
 * list of what moves when. `prevKind` is the kind of the step before this one — a push
 * after a push is a side pot and waits `sidePotGap`.
 */
function flightsFor(step: Step, before: TableState, a: Anchors, d: Durations, prevKind: string | null): Flight[] {
  const heroIndex = before.seats.findIndex((s) => s.isSelf);
  const fade = d.reduced;
  const mk = (f: Omit<Flight, "id" | "fade" | "delay" | "swell" | "arc"> & Partial<Pick<Flight, "delay" | "swell" | "arc">>): Flight =>
    ({ id: nextId++, fade, delay: 0, swell: 1, arc: 0, ...f });
  const dist = (p: Point, q: Point) => Math.hypot(q.x - p.x, q.y - p.y);
  const out: Flight[] = [];

  switch (step.kind) {
    case "deal": {
      const hero = step.seat === heroIndex;
      const target = a.cards(step.seat, hero);
      if (!target.at) break;
      const from = a.dealer();
      const seatCard = a.cards(-1, false).width;
      out.push(mk({
        kind: "card", from, to: target.at, width: target.width, dur: step.land,
        /* The peek fans its two cards; the copy arrives at the angle the card will lie at. */
        rotateTo: step.index === 0 ? target.angle : -target.angle,
        /* The hero's cards fly at seat size and grow only over the last part. */
        growFrom: hero ? seatCard / Math.max(1, target.width) : undefined,
        arc: dist(from, target.at) * SHAPE.betArc,
      }));
      break;
    }
    case "post":
    case "action": {
      if (step.kind === "action" && step.action.kind === "fold") {
        const hero = step.seat === heroIndex;
        const from = a.cards(step.seat, hero);
        if (!from.at) break;
        const to = a.dealer();
        const seatCard = a.cards(-1, false).width;
        for (let i = 0; i < 2; i++) {
          out.push(mk({
            kind: "card", from: { x: from.at.x + (i - 0.5) * from.width * 0.6, y: from.at.y }, to,
            width: from.width, dur: step.dur, delay: i * d.muckStagger,
            rotateTo: i === 0 ? -12 : 12, muck: true,
            /* The hero's big cards shrink to seat size on the way in; a seat's just settle. */
            swell: hero ? seatCard / Math.max(1, from.width) : SHAPE.muckEndScale,
          }));
        }
        break;
      }
      if (step.kind === "action" && !step.chips) break;
      const hero = step.seat === heroIndex;
      const from = a.stackNum(step.seat, hero);
      const to = a.bet(step.seat);
      if (!from || !to) break;
      const seat = before.seats[step.seat];
      const amount = step.kind === "post" ? step.amount
        : Math.max(0, (step.action.to ?? 0) - (seat?.committed ?? 0));
      out.push(mk({
        kind: "chips", from, to, width: a.chip(), dur: step.dur, stacks: breakdown(amount),
        swell: SHAPE.betScale, arc: dist(from, to) * SHAPE.betArc,
      }));
      break;
    }
    case "collect": {
      const pot = a.potChips();
      if (!pot) break;
      const n = before.seats.length;
      clockwiseFromButton(step.seats, before.button, n).forEach((seat, i) => {
        const from = a.bet(seat);
        const committed = before.seats[seat]?.committed ?? 0;
        if (!from || committed <= 0) return;
        out.push(mk({
          kind: "chips", from, to: pot, width: a.chip(), dur: step.dur, stacks: breakdown(committed),
          delay: i * d.collectStagger, arc: dist(from, pot) * SHAPE.betArc,
        }));
      });
      break;
    }
    case "push": {
      const pot = a.potChips();
      const to = a.stackNum(step.seat, step.seat === heroIndex);
      if (!pot || !to) break;
      out.push(mk({
        kind: "chips", from: pot, to, width: a.chip(), dur: step.dur, stacks: breakdown(step.amount),
        /* A side pot follows the main pot, a beat after it has landed. */
        delay: prevKind === "push" ? d.sidePotGap : 0,
        swell: SHAPE.pushScale, arc: dist(pot, to) * SHAPE.betArc,
      }));
      break;
    }
    /* Refunds land quietly with the hand's end (Phase 3 review, answer 6): no flight. */
    case "refund":
    default:
      break;
  }
  return out;
}

/* ---------------------------------------------------------------- bubbles --- */

/** One bubble to draw: whose seat, and what it says. */
export interface Bubble {
  seat: number;
  item: TauntItem;
  /** The player's own. Placed first, so it is the last one given up. */
  hero?: boolean;
}

/**
 * Where a bubble goes, worked out rather than declared.
 *
 * ==================== WHY THIS IS JAVASCRIPT AND CSS WAS NOT ==================
 * Phase 2 placed the bubble in CSS, lifted off its seat by an expression in
 * `--taunt-lift` that reasons about the seat's bearing, whether it has a peek and how
 * tall a card is. That expression is genuinely clever and it cannot do the one thing
 * this phase requires: the Phase 2 review's demand is that **a bubble never covers a
 * card — it moves, it is not cut**, and "does this rectangle overlap that one" is not
 * a thing a stylesheet can ask. The old rule handled it by capping the bubble's width
 * to 1.15 name plates and truncating with an ellipsis, which is cutting.
 *
 * So the bubble moved into the flight layer, where the machinery for measuring the
 * felt by its `data-td` anchors already lives, and its position is chosen from a list
 * of candidates: above the seat first, then shifted along, then higher, then out along
 * the seat's own bearing away from the middle of the table, then below. The first
 * candidate that is fully inside the frame and clears every card, the board, the pot
 * and every bubble already placed wins; among candidates that also clear the name
 * plates and bets, the first of those wins instead.
 *
 * A bubble that cannot be placed anywhere is NOT DRAWN. That is the honest end of
 * "it moves, it is not cut": on the tightest frame in the product — a phone on its
 * side, a 346x249 felt with nine seats and a five-card board — there are positions
 * for some of nine simultaneous bubbles and not for all of them. The hero's is placed
 * FIRST so that the one bubble a player pressed a button for is the last to be given
 * up, and the sweep counts what gets dropped.
 * ==========================================================================
 */
function placeBubble(
  box: { w: number; h: number }, seat: { at: Point; size: number }, out: Point,
  bounds: Box, hard: Box[], soft: Box[], placed: Box[],
): Box | null {
  const gap = 6;
  const up = seat.size / 2 + gap + box.h / 2;
  const side = box.w * 0.5;
  const step = box.h * 0.95;
  const outX = out.x * (seat.size / 2 + gap + box.w / 2);
  const outY = out.y * (seat.size / 2 + gap + box.h / 2);
  /*
   * Offsets from the portrait's centre. Every one of them is a place a bubble could
   * plausibly belong to this seat; which one is used is decided by distance below, not
   * by the order here — an ordered list quietly encodes "up is better than near", and
   * up-and-a-long-way-sideways is not better than near.
   */
  const candidates: [number, number][] = [
    [0, -up],
    [-side, -up], [side, -up],
    [0, -up - step],
    [-side, -up - step], [side, -up - step],
    [outX, outY],
    [outX * 1.5, outY * 1.5],
    [0, -up - step * 2],
    [-side * 1.5, -up], [side * 1.5, -up],
    [0, up], [-side, up], [side, up],
    [-side * 1.5, -up - step], [side * 1.5, -up - step],
    [0, up + step], [-side, up + step], [side, up + step],
    /*
     * PHASE 8 BOX 0(1): two more, a tier higher and to either side. With the dealer
     * button an obstacle, a player who is all in on the button had nowhere left: the
     * wide pile took the first tier, the D the middle of the second and third, and the
     * one bubble the player pressed a button for was the one given up. Distance still
     * ranks them, so they are chosen only where nothing nearer is legal.
     */
    [-side, -up - step * 2], [side, -up - step * 2],
  ];

  /*
   * Nearest wins, among the ones that are legal.
   *
   * A bubble is somebody speaking, so the reader has to be able to tell WHO — and the
   * tail can only lean so far. The first version took the first candidate that fit and
   * put Orla's line in the middle of the table with a tail pointing at green baize.
   * Distance from the seat is the whole of the ranking; the soft obstacles break ties.
   */
  let best: { spot: Box; cost: number } | null = null;
  for (const [dx, dy] of candidates) {
    const cx = seat.at.x + dx, cy = seat.at.y + dy;
    const b: Box = { l: cx - box.w / 2, t: cy - box.h / 2, r: cx + box.w / 2, b: cy + box.h / 2 };
    if (!within(b, bounds)) continue;
    if (hard.some((o) => overlaps(b, o))) continue;
    if (placed.some((o) => overlaps(b, o))) continue;
    /* Distance from the seat, plus a penalty for sitting on a portrait. */
    const cost = Math.hypot(dx, dy) + (soft.some((o) => overlaps(b, o)) ? box.h * 1.5 : 0);
    if (!best || cost < best.cost) best = { spot: b, cost };
  }
  return best?.spot ?? null;
}

/**
 * The bubbles, drawn in the flight layer and placed after they are measured.
 *
 * Two passes in one frame: React renders each bubble at its seat's own centre (never
 * painted — `useLayoutEffect` runs before paint), the effect measures the real boxes
 * and writes `left`/`top`, and the transition takes it from there. Re-measured whenever
 * the set of bubbles changes and on a resize, which is the only other thing that moves
 * a seat.
 */
export function BubbleLayer({ bubbles, durations, persist = false }: {
  bubbles: readonly Bubble[];
  durations: Durations;
  /**
   * The bubble stays up instead of fading out on its own.
   *
   * The fade-out is a CSS animation with a delay, because the element's lifetime is
   * owned by `useVoice` and a React unmount cannot be animated from the stylesheet.
   * That is right at a live table and wrong in the harness, where a bubble is a FIXTURE
   * and has to still be there when the screenshot is taken — a sweep that measured
   * five hundred invisible bubbles would pass for the wrong reason.
   */
  persist?: boolean;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onResize = () => setTick((t) => t + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useLayoutEffect(() => {
    const layer = wrap.current;
    const root = layer?.parentElement;
    if (!layer || !root) return;
    const a = new Anchors(root);
    const bounds = a.bounds();
    const { hard, soft } = a.obstacles();
    const placed: Box[] = [];
    /* The hero first, then everybody else: see the note on `placeBubble`. */
    const els = [...layer.querySelectorAll<HTMLElement>("[data-td=taunt]")];
    els.sort((x, y) => Number(y.dataset.hero === "1") - Number(x.dataset.hero === "1"));
    for (const el of els) {
      const seat = Number(el.dataset.seat);
      const anchor = a.seatAvatar(seat);
      /* Un-drop it before measuring: a bubble dropped on the last pass has no box. */
      el.style.display = "";
      const r = el.getBoundingClientRect();
      if (!anchor || r.width < 1) { el.style.display = "none"; el.dataset.dropped = "1"; continue; }
      const spot = placeBubble({ w: r.width, h: r.height }, anchor, a.outward(anchor.at),
                               bounds, hard, soft, placed);
      /*
       * A bubble with nowhere to go is `display: none`, not `visibility: hidden`.
       *
       * Hidden, it keeps its box — sitting at the layer's own top-left corner, where it
       * was rendered before being measured — and every measurement downstream, the
       * sweep's own "is it inside the frame" among them, goes on counting a rectangle
       * nobody can see. Ten of the first four hundred and eighty swept failed on
       * exactly that.
       */
      if (!spot) { el.style.display = "none"; el.dataset.dropped = "1"; continue; }
      placed.push(spot);
      el.dataset.dropped = "";
      el.style.left = `${spot.l + (spot.r - spot.l) / 2}px`;
      el.style.top = `${spot.t + (spot.b - spot.t) / 2}px`;
      /* The tail leans toward the seat: a fraction of the bubble's own width, clamped
         so it stays on the bubble's edge rather than floating beside it. */
      const dx = anchor.at.x - (spot.l + (spot.r - spot.l) / 2);
      const frac = Math.max(-0.38, Math.min(0.38, dx / Math.max(1, r.width)));
      el.style.setProperty("--tail", `${50 + frac * 100}%`);
      el.style.setProperty("--tail-up", anchor.at.y < spot.t ? "1" : "0");
    }
  }, [bubbles, tick]);

  if (bubbles.length === 0) return null;
  return (
    <div className="td-flight td-bubbles" ref={wrap} aria-hidden="true"
         style={{
           "--td-ms-taunt-in": `${durations.tauntIn}ms`,
           "--td-ms-taunt-out": `${persist ? 0 : durations.tauntOut}ms`,
           /* An hour is "never" for a hand of poker, and it is one number rather than
              a second animation nobody would find when the first one misbehaved. */
           "--td-ms-taunt-hold": persist ? "3600000ms" : `${durations.tauntHold}ms`,
         } as CSSProperties}>
      {bubbles.map((b) => (
        <span key={`${b.seat}:${b.item.id}`} className={`td-taunt td-taunt-${b.item.kind}`}
              data-td="taunt" data-seat={b.seat} data-hero={b.hero ? "1" : undefined}>
          {b.item.kind === "sticker" && stickerUrl(b.item.sticker)
            ? <img src={stickerUrl(b.item.sticker)!} alt="" />
            : b.item.text}
        </span>
      ))}
    </div>
  );
}

/**
 * What turns a step into flights, for a table that is not poker's.
 *
 * PHASE 8: the blackjack table is a third composition of the same parts and flies the
 * same chips and cards, from different places — a shoe rather than a deck, a circle
 * rather than a bet spot, the house's tray rather than the pot. It hands this layer its
 * own planner (`blackjack-flight.ts`) and gets everything else — the copies, the arcs,
 * the settle, reduced motion — unchanged. Poker passes nothing and runs `flightsFor`
 * exactly as before.
 */
export type FlightPlanner<S, B> =
  (step: S, before: B, root: HTMLElement, durations: Durations, prevKind: string | null) => Flight[];

export function FlightLayer<S extends { kind: string; dur: number; land: number } = Step, B = TableState>(
  { bus, durations, plan }: { bus: FlightBus<S, B>; durations: Durations; plan?: FlightPlanner<S, B> },
) {
  const ref = useRef<HTMLDivElement>(null);
  const [flights, setFlights] = useState<Flight[]>([]);
  /* Read at flight time, not subscription time, so a pace change applies at once. */
  const dur = useRef(durations);
  dur.current = durations;
  const prevKind = useRef<string | null>(null);
  const planRef = useRef(plan);
  planRef.current = plan;

  useEffect(() => bus.subscribe((step, before) => {
    const layer = ref.current;
    const root = layer?.parentElement;
    if (!layer || !root) return;
    const made = planRef.current
      ? planRef.current(step, before, root, dur.current, prevKind.current)
      : flightsFor(step as unknown as Step, before as unknown as TableState, new Anchors(root), dur.current, prevKind.current);
    prevKind.current = step.kind;
    if (made.length) setFlights((f) => [...f, ...made]);
  }), [bus]);

  return (
    /*
     * `aria-hidden`, and the bubbles are inside it.
     *
     * Everything in this layer is a picture of something that is also somewhere else:
     * a flying chip is a bet the felt is about to draw, and a bubble's WORDS are read
     * out by the live region in `TopDownTable` rather than from here. A screen reader
     * hearing "Well now, look at that" twice is worse than hearing it once.
     */
    <div className="td-flight" ref={ref} aria-hidden="true">
      {flights.map((f) => (
        <Flying key={f.id} flight={f} onDone={() => setFlights((all) => all.filter((x) => x.id !== f.id))} />
      ))}
    </div>
  );
}

/** One copy in the air. Rendered at the source, animated to the destination. */
function Flying({ flight, onDone }: { flight: Flight; onDone: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const done = useRef(onDone);
  done.current = onDone;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const f = flight;
    const opts = (extra: Partial<KeyframeAnimationOptions> = {}): KeyframeAnimationOptions =>
      ({ duration: Math.max(1, f.dur), delay: f.delay, fill: "forwards", ...extra });
    const base = "translate(-50%, -50%)";
    const dx = f.to.x - f.from.x, dy = f.to.y - f.from.y;
    let anim: Animation;

    if (f.fade && f.muck) {
      /* Reduced motion, into the muck: the cards fade where they lie. */
      anim = el.animate([{ opacity: 1 }, { opacity: 0 }], opts({ easing: "ease-out" }));
    } else if (f.fade) {
      el.style.left = `${f.to.x}px`; el.style.top = `${f.to.y}px`;
      anim = el.animate([{ opacity: 0 }, { opacity: 1 }], opts({ easing: "ease-out" }));
    } else if (f.muck) {
      /* Slide, turn, shrink; visible for the first part, gone by the time it lands. */
      anim = el.animate([
        { transform: `${base} translate(0, 0) rotate(0deg) scale(1)`, opacity: 1, offset: 0 },
        { opacity: 1, offset: SHAPE.muckFadeFrom },
        { transform: `${base} translate(${dx}px, ${dy}px) rotate(${f.rotateTo ?? 0}deg) scale(${f.swell})`, opacity: 0, offset: 1 },
      ], opts({ easing: "cubic-bezier(.3, .6, .4, 1)" }));
    } else if (f.kind === "card") {
      /*
       * A dealt card: leaves the deck already turning, arrives at the peek's angle,
       * grows from `dealScale` to 1 with a slight arc. The hero's grows from seat
       * size only over the last part (`dealGrowFrom`), so it crosses the rail small.
       */
      const g0 = f.growFrom ?? SHAPE.dealScale;
      const gMid = f.growFrom ?? 1;
      const rot = f.rotateTo ?? 0;
      anim = el.animate([
        { transform: `${base} translate(0, 0) rotate(0deg) scale(${g0})`, offset: 0 },
        { transform: `${base} translate(${dx * SHAPE.dealGrowFrom}px, ${dy * SHAPE.dealGrowFrom - f.arc}px) rotate(${rot * SHAPE.dealGrowFrom}deg) scale(${gMid})`, offset: SHAPE.dealGrowFrom },
        { transform: `${base} translate(${dx}px, ${dy}px) rotate(${rot}deg) scale(1)`, offset: 1 },
      ], opts({ easing: "cubic-bezier(.2, .7, .2, 1)" }));
    } else {
      /* Chips: a short pile that arcs a little and swells at mid-flight. */
      anim = el.animate([
        { transform: `${base} translate(0, 0) scale(1)`, offset: 0 },
        { transform: `${base} translate(${dx / 2}px, ${dy / 2 - f.arc}px) scale(${f.swell})`, offset: 0.5 },
        { transform: `${base} translate(${dx}px, ${dy}px) scale(1)`, offset: 1 },
      ], opts({ easing: "cubic-bezier(.2, .7, .2, 1)" }));
    }
    let finished = false;
    const finish = () => { if (!finished) { finished = true; done.current(); } };
    anim.onfinish = finish;
    anim.oncancel = finish;
    return () => { anim.cancel(); };
  }, [flight]);

  const style: CSSProperties = {
    left: `${flight.from.x}px`, top: `${flight.from.y}px`, width: `${flight.width}px`,
  };
  return (
    <div ref={ref} className={`td-flying td-flying-${flight.kind}`} style={style}>
      {flight.kind === "chips" && <ChipStacks stacks={flight.stacks} width={`${flight.width}px`} />}
      {flight.kind === "card" && <PlayingCard kind="seat" hidden />}
    </div>
  );
}

/* --------------------------------------------------------------- numbers --- */

/**
 * A number that counts to its new value over `ms`, for stacks and the pot.
 *
 * Rendered through `fmt`, so it is in whatever unit the table is showing. With no
 * `ms` — the harness, the classic felt — it is just the number.
 */
export function Counted({ value, ms }: { value: number; ms?: number }) {
  const { fmt } = useMoney();
  const [shown, setShown] = useState(value);
  const fromRef = useRef(value);

  useEffect(() => {
    const from = fromRef.current;
    if (!ms || ms <= 0 || from === value) { fromRef.current = value; setShown(value); return; }
    const start = performance.now();
    /* Count in whole chips when both ends are whole, so a stack never passes
       through "97.31" on its way from 100 to 97. Quarter chips otherwise. */
    const unit = Number.isInteger(from) && Number.isInteger(value) ? 1 : 0.25;
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / ms);
      const eased = 1 - (1 - p) * (1 - p);
      setShown(Math.round((from + (value - from) * eased) / unit) * unit);
      if (p < 1) raf = requestAnimationFrame(tick);
      else { fromRef.current = value; setShown(value); }
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); fromRef.current = value; };
  }, [value, ms]);

  return <>{fmt(shown)}</>;
}
